import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group flex gap-3 p-2 rounded-lg hover:bg-primary-50 transition-all duration-200 cursor-pointer"
        data-product-shop=""
      >
        <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={comic.cover}
            alt={comic.title}
            title={`${comic.title} - 亲亲漫画网下拉式在线看`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex flex-col justify-center min-w-0">
          <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
            {comic.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs px-1.5 py-0.5 rounded ${comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-600'}`}>
              {comic.status}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group relative flex flex-col rounded-lg overflow-hidden bg-background-50 cursor-pointer"
        data-product-shop=""
      >
        <div className="relative w-full aspect-[3/4] overflow-hidden">
          <img
            src={comic.cover}
            alt={comic.title}
            title={`${comic.title} - 亲亲漫画网免费下拉式看漫画`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="text-white text-lg font-semibold leading-tight">
              {comic.title}
            </h3>
            <p className="text-white/80 text-sm mt-1 line-clamp-2">
              {comic.description}
            </p>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
      data-product-shop=""
    >
      <div className="relative w-full aspect-[3/4] overflow-hidden">
        <img
          src={comic.cover}
          alt={comic.title}
          title={`${comic.title} - 亲亲漫画网下拉式全集在线看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {comic.isHot && (
          <span className="absolute top-2 left-2 bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
            HOT
          </span>
        )}
        {comic.isNew && (
          <span className="absolute top-2 left-2 bg-accent-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
            NEW
          </span>
        )}
        <div className="absolute top-2 right-2 bg-background-950/70 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1">
          <i className="ri-star-fill text-accent-500 text-[10px]"></i>
          {comic.rating}
        </div>
      </div>
      <div className="p-3 flex flex-col gap-1.5">
        <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
          {comic.title}
        </h4>
        <p className="text-xs text-foreground-500 truncate">{comic.author}</p>
        <div className="flex items-center justify-between mt-1">
          <span className={`text-xs px-1.5 py-0.5 rounded ${comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-600'}`}>
            {comic.status}
          </span>
          <span className="text-xs text-foreground-400 flex items-center gap-1">
            <i className="ri-eye-line text-[10px]"></i>
            {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}
          </span>
        </div>
      </div>
    </Link>
  );
}